import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import { ORANGE } from 'constants/colors';

import { brightness } from 'utils/misc';

import {
  Box,
  Button,
  Collapse,
  DialogActions,
  Grid,
  Typography
} from '@mui/material';

import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledTimePicker,
  Dialog,
  FontAwesomeSvgIcon
} from 'components/shared';

import {
  useDeleteClientAbsencePeriod,
  useEndClientAbsencePeriod
} from '../../mutations';
import deleteAbsenceSchema from './delete-absence-periods-schema';
import { ClientAbsence } from 'RestAPIs/absence';
import { useEffect } from 'react';

const schema = deleteAbsenceSchema();

type Form = InferType<typeof schema>;

type Props = {
  title: string;
  open: boolean;
  handleClose: () => void;
  clientId: string | undefined;
  clientAbsence: ClientAbsence | undefined;
};

export default function DeleteAbsencePeriodDialog({
  open,
  handleClose,
  title,
  clientId,
  clientAbsence
}: Props) {
  const { t } = useTranslation('common');
  const { mutate: endClientAbsencePeriod } = useEndClientAbsencePeriod();
  const { mutate: deleteClientAbsencePeriod } = useDeleteClientAbsencePeriod();
  const { control, handleSubmit, watch, reset } = useForm<Form>({
    resolver: yupResolver(schema)
  });
  const { deletePeriod } = watch();

  useEffect(() => {
    reset({
      deletePeriod: false,
      endDate: dayjs().startOf('day'),
      endTime: dayjs(null)
    });
  }, [clientAbsence?.end]);

  function onSubmit(data: Form) {
    const { deletePeriod, endDate, endTime } = data;
    if (!clientId || !clientAbsence) return;

    const basePayload = {
      clientId,
      clientAbsenceId: clientAbsence.clientAbsenceId
    };

    if (deletePeriod) {
      deleteClientAbsencePeriod(basePayload);
    } else {
      const newEnd = endDate.hour(endTime.hour()).minute(endTime.minute());

      const payload = {
        ...basePayload,
        end: newEnd.toISOString()
      };

      endClientAbsencePeriod(payload);
    }
    handleClose();
  }

  return (
    <Dialog open={open} handleClose={handleClose} title={title}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ControlledCheckbox
              control={control}
              name="deletePeriod"
              label="Verwijder gehele periode"
            />
          </Grid>
          <Grid item xs={12}>
            <Collapse in={deletePeriod}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  p: 1,
                  bgcolor: brightness(ORANGE, 70),
                  border: `1px solid ${ORANGE}`,
                  borderRadius: '4px'
                }}
              >
                <FontAwesomeSvgIcon
                  icon={faExclamationTriangle}
                  color="warning"
                />
                <Typography fontSize="small">
                  Let op: u verwijdert hiermee de gehele afwezigheidsperiode
                </Typography>
              </Box>
            </Collapse>
          </Grid>
          <Grid item xs={12}>
            <Collapse in={!deletePeriod}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <ControlledDatePicker
                    name="endDate"
                    control={control}
                    label={t('endDate')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ControlledTimePicker
                    name="endTime"
                    control={control}
                    label={t('endTime')}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
        <DialogActions sx={{ mt: 3 }}>
          <Button
            type="button"
            variant="text"
            color="dark"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" variant="contained" color="dark">
            {t('save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
